import { createFileRoute, Link } from "@tanstack/react-router";
import heroTitleServices from "@/assets/hero/hero-title-services.jpg.asset.json";

const HERO = {
  src: heroTitleServices.url,
  alt: "Smiling investor with long blonde hair relaxing in his backyard, reading the Simply Approved title and escrow services page on his smartphone in warm evening light",
};
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { partnerBySlug } from "@/data/partners";
import { PartnerCTA, PartnerDisclosure } from "@/components/site/PartnerBlocks";
import { WhyYouNeedIt, HowItWorks } from "@/components/site/BusinessSetupBlocks";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

export const Route = createFileRoute("/business-setup/title-services")({
  head: () =>
    buildHead({
      title: "Title & Escrow Services for Investor Closings | Simply Approved",
      description:
        "Title search, commitment, escrow, and closing coordination for investment property purchases and refinances vested in an LLC.",
      url: "/business-setup/title-services",
      type: "article",
      keywords:
        "title and escrow services for investors, title company entity vesting LLC, title commitment investment property, escrow closing DSCR loan, title services fix and flip closing, LLC vesting title insurance, payoff and assignment title, investor-focused title company, closing coordination business purpose loan, title search rental property, bridge loan title services",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Business Setup", url: "/business-setup" },
        { name: "Title & escrow services", url: "/business-setup/title-services" },
      ],
      service: { name: "Title & Escrow Services for Investors" },
      image: HERO.src,
      imageAlt: HERO.alt,
    }),
  component: TitlePage,
});

function TitlePage() {
  const partner = partnerBySlug("closed-title")!;
  return (
    <SiteShell>
      <PageHero
        eyebrow="Business Setup"
        title="Title and escrow for investor closings"
        subtitle="Title work runs alongside underwriting. Getting the entity, vesting, and payoff details right early is what keeps a closing on schedule."
        image={HERO}
      />
      <PageEssentials />
      <WhyYouNeedIt
        service={"title and escrow services"}
        intro={"Title work decides whether a deal closes on time. An investor closing has moving parts a retail closing does not \u2014 entity vesting, assignments, payoffs on existing short-term financing, and tight timelines \u2014 and a title company that handles those every day keeps the file moving."}
        reasons={[
          { title: "Clear title is a condition of funding", body: "The lender's policy and a clean commitment are required before we can fund. Liens and vesting issues surface here." },
          { title: "Entity vesting has to be handled correctly", body: "Deeds, resolutions, and signing authority for an LLC differ from a personal purchase. Errors mean re-signing after closing." },
          { title: "Investor timelines are tight", body: "Bridge, hard money, and flip closings often run on days, not weeks. Escrow needs to keep pace with the contract." },
          { title: "Payoffs and assignments need experience", body: "Existing short-term loan payoffs, contract assignments, and back-to-back closings are routine for an investor-focused title desk." },
        ]}
      />
      <Section>
        <SectionHeader eyebrow="What title handles" title="Pieces of an entity-vested closing" />
        <ul className="mt-6 grid gap-3 text-sm md:grid-cols-2">
          {[
            "Title search and commitment on the subject property",
            "Vesting in the LLC exactly as the entity is filed",
            "Lender's title policy and endorsements",
            "Payoff statements on any existing liens",
            "Escrow of earnest money and closing funds",
            "Recording and post-closing document delivery",
          ].map((i) => (
            <li key={i} className="rounded-lg border border-border bg-card p-4">{i}</li>
          ))}
        </ul>
        <HowItWorks
          steps={[
            "Open title as soon as the contract is executed, with the entity listed as buyer.",
            "Clear any exceptions on the commitment \u2014 liens, judgments, or vesting corrections.",
            "Coordinate the closing disclosure, wire instructions, and signing with your loan officer.",
          ]}
        />
        <PartnerCTA partner={partner} />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/apply" className="rounded-md bg-brand-gold px-5 py-3 text-sm font-semibold text-primary-foreground">Start My Approval</Link>
          <Link to="/business-setup/llc" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">Set up your LLC</Link>
        </div>
        <PartnerDisclosure />
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
